/**
 * `chat:` / `c:` (PLAN §2.2, S2): channels and groups. DMs are People rows —
 * a person with a DM is one row, never two — and the `chat:` scope runs the
 * people provider alongside this one (runSearch SCOPE_FAMILIES), so
 * `chat:kenan kobic` finds the DM partner by name in any word order.
 */
import { Hash, Users, MessagesSquare, BellOff, Bell, MailCheck, CornerDownLeft } from 'lucide-react';
import { ORDER } from './order';
import { selectTop } from '../localSearch';
import i18n from '../../i18n';

function tsOf(channel) {
  const raw = channel?.last_message?.inserted_at || channel?.updated_at || channel?.inserted_at;
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(t) ? t : undefined;
}

export function chatResults(channels, { currentUserId, teams = [] } = {}) {
  const me = currentUserId != null ? String(currentUserId) : null;
  const teamName = (id) => (teams.find((tm) => String(tm.id) === String(id)) || {}).name;
  const out = [];
  for (const ch of channels || []) {
    if (!ch || ch.id == null || ch.kind === 'dm') continue;
    const others = (ch.members || []).filter((m) => String(m.user_id) !== me);
    const groupName = others.map((m) => m.user?.name).filter(Boolean).join(', ');
    const title = ch.kind === 'channel' ? ch.name || i18n.t('palette.fallback.channel') : ch.name || groupName || i18n.t('palette.fallback.group');
    const count = (ch.members || []).length;
    const parts = [];
    if (ch.team_id != null && teamName(ch.team_id)) parts.push(teamName(ch.team_id));
    parts.push(i18n.t('palette.chats.members', { count }));
    out.push({
      provider: 'chats',
      id: String(ch.id),
      title,
      subtitle: parts.join(' · '),
      icon: ch.kind === 'channel' ? Hash : Users,
      ts: tsOf(ch),
      searchFields: ch.kind === 'group' && ch.name && groupName ? [{ text: groupName, weight: 0.4 }] : [],
      payload: { channelId: ch.id, kind: ch.kind, muted: !!ch.muted, serverSeq: ch.server_seq ?? null, teamId: ch.team_id ?? null },
    });
  }
  return out;
}

/**
 * @param {{ getChannels: () => object[], getTeams: () => object[], openChat: Function,
 *           setMuted: (id, muted) => Promise<object|null>, markRead: (id, seq) => void|Promise<void> }} deps
 */
export default function create(deps) {
  let cacheKey = null;
  let cache = [];
  const list = (ctx) => {
    const channels = deps.getChannels();
    if (channels !== cacheKey) {
      cacheKey = channels;
      cache = chatResults(channels, { currentUserId: ctx.currentUserId, teams: deps.getTeams?.() || [] });
    }
    return cache;
  };
  return {
    id: 'chats',
    prefixes: ['chat', 'c', 'chats', 'dm', 'channel', 'channels'],
    labelKey: 'palette.scope.chats',
    icon: MessagesSquare,
    order: ORDER.chats,
    page: 'chat.panel',
    kind: 'local-sync',
    available: (ctx) => !!ctx.currentUserId,
    search(req) {
      return selectTop(list(req.ctx), req, { emptyOrder: (a, b) => (b.ts || 0) - (a.ts || 0) });
    },
    async recent(ids, ctx) {
      const all = list(ctx);
      return ids.map((id) => all.find((r) => r.id === id)).filter(Boolean);
    },
    actions(result) {
      const muted = !!result.payload?.muted;
      return [
        { id: 'open', titleKey: 'palette.action.open', icon: CornerDownLeft, run: (r) => deps.openChat({ channelId: r.payload.channelId }) },
        {
          id: muted ? 'unmute' : 'mute',
          titleKey: muted ? 'palette.action.unmute' : 'palette.action.mute',
          icon: muted ? Bell : BellOff,
          run: (r) => deps.setMuted(r.payload.channelId, !muted),
        },
        { id: 'markRead', titleKey: 'palette.action.markRead', icon: MailCheck, run: (r) => deps.markRead(r.payload.channelId, r.payload.serverSeq) },
      ];
    },
  };
}
